import { MapPin, Briefcase, Clock, ArrowRight, Heart, Zap, Users, Rocket, Star } from 'lucide-react';
import { Link } from 'react-router-dom';

export default function Careers() {
  const openings = [
    {
      title: 'Senior Machine Learning Engineer',
      department: 'Engineering',
      location: 'Chicago, IL / Remote',
      type: 'Full-time',
      description: 'Build and deploy cutting-edge ML models for healthcare applications.',
      color: 'from-cyan-500 to-blue-600'
    },
    {
      title: 'Healthcare Product Manager',
      department: 'Product',
      location: 'Chicago, IL / Hybrid',
      type: 'Full-time',
      description: 'Lead product strategy and development for our AI healthcare solutions.',
      color: 'from-purple-500 to-indigo-600'
    },
    {
      title: 'Clinical Data Scientist',
      department: 'Data Science',
      location: 'Remote',
      type: 'Full-time',
      description: 'Analyze clinical data and develop predictive models to improve patient outcomes.',
      color: 'from-emerald-500 to-teal-600'
    },
    {
      title: 'UX/UI Designer',
      department: 'Design',
      location: 'Remote',
      type: 'Full-time',
      description: 'Design intuitive interfaces for complex medical AI applications.',
      color: 'from-pink-500 to-rose-600'
    },
    {
      title: 'DevOps Engineer',
      department: 'Engineering',
      location: 'Chicago, IL / Remote',
      type: 'Full-time',
      description: 'Build and maintain scalable infrastructure for healthcare AI systems.',
      color: 'from-orange-500 to-red-600'
    },
    {
      title: 'Sales Development Representative',
      department: 'Sales',
      location: 'Chicago, IL / Hybrid',
      type: 'Full-time',
      description: 'Connect with healthcare organizations to demonstrate our AI solutions.',
      color: 'from-yellow-500 to-orange-600'
    }
  ];

  const benefits = [
    {
      icon: Heart,
      title: 'Health & Wellness',
      description: 'Comprehensive medical, dental, and vision coverage for you and your family',
      color: 'from-red-500 to-pink-600'
    },
    {
      icon: Zap,
      title: 'Growth & Learning',
      description: '$5,000 annual learning budget plus access to conferences and training',
      color: 'from-yellow-500 to-orange-600'
    },
    {
      icon: Users,
      title: 'Work-Life Balance',
      description: 'Flexible hours, remote work options, and unlimited PTO policy',
      color: 'from-emerald-500 to-teal-600'
    },
    {
      icon: Rocket,
      title: 'Equity & Bonuses',
      description: 'Competitive salary with equity packages and performance bonuses',
      color: 'from-purple-500 to-indigo-600'
    }
  ];

  const perks = [
    '401(k) with 4% match',
    'Home office stipend',
    'Parental leave',
    'Mental health support',
    'Team retreats',
    'Free lunch on-site',
    'Gym membership',
    'Commuter benefits'
  ];

  return (
    <div className="min-h-screen bg-[#030712]">
      {/* Background Effects */}
      <div className="fixed inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-0 left-1/4 w-[600px] h-[600px] bg-cyan-500/10 rounded-full blur-[120px]" />
        <div className="absolute bottom-0 right-1/4 w-[500px] h-[500px] bg-purple-500/10 rounded-full blur-[100px]" />
      </div>

      {/* Hero Section */}
      <section className="relative z-10 pt-32 pb-16 px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto text-center">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-purple-500/10 border border-purple-500/20 rounded-full mb-6">
            <Rocket className="w-4 h-4 text-purple-400" />
            <span className="text-purple-400 text-sm font-medium">Join Our Team</span>
          </div>
          <h1 className="text-4xl lg:text-6xl font-bold text-white mb-6">
            Join Our{' '}
            <span className="bg-gradient-to-r from-cyan-400 to-blue-500 bg-clip-text text-transparent">
              Mission
            </span>
          </h1>
          <p className="text-xl text-gray-400 leading-relaxed">
            Help us transform healthcare through artificial intelligence. Build products
            that make a real difference in people's lives.
          </p>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="relative z-10 py-16 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-white mb-4">Why PulseNex?</h2>
            <p className="text-gray-400 text-lg max-w-2xl mx-auto">
              Join a team of passionate professionals working at the cutting edge of healthcare innovation.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-16">
            {benefits.map((benefit, index) => (
              <div
                key={index}
                className="bg-gradient-to-br from-white/[0.07] to-white/[0.02] border border-white/10 rounded-2xl p-6 text-center hover:border-cyan-500/30 transition-all group"
              >
                <div className={`inline-flex p-4 rounded-2xl bg-gradient-to-br ${benefit.color} mb-4 group-hover:scale-110 transition-transform`}>
                  <benefit.icon className="w-8 h-8 text-white" />
                </div>
                <h3 className="text-lg font-bold text-white mb-2">{benefit.title}</h3>
                <p className="text-sm text-gray-400 leading-relaxed">{benefit.description}</p>
              </div>
            ))}
          </div>

          {/* Additional Perks */}
          <div className="bg-gradient-to-br from-white/[0.05] to-transparent border border-white/10 rounded-3xl p-8 mb-16">
            <h3 className="text-xl font-bold text-white mb-6 text-center">Additional Perks</h3>
            <div className="flex flex-wrap justify-center gap-3">
              {perks.map((perk, index) => (
                <span
                  key={index}
                  className="px-4 py-2 bg-white/5 border border-white/10 rounded-full text-gray-300 text-sm"
                >
                  {perk}
                </span>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Open Positions */}
      <section className="relative z-10 py-16 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-emerald-500/10 border border-emerald-500/20 rounded-full mb-6">
              <Star className="w-4 h-4 text-emerald-400" />
              <span className="text-emerald-400 text-sm font-medium">{openings.length} Open Positions</span>
            </div>
            <h2 className="text-3xl font-bold text-white mb-4">Current Openings</h2>
            <p className="text-gray-400 text-lg">Find your next opportunity at PulseNex</p>
          </div>

          <div className="space-y-4">
            {openings.map((job, index) => (
              <div
                key={index}
                className="group bg-gradient-to-br from-white/[0.07] to-white/[0.02] border border-white/10 rounded-2xl p-6 hover:border-cyan-500/30 transition-all"
              >
                <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
                  <div className="flex-1">
                    <div className="flex items-start gap-4">
                      <div className={`w-12 h-12 rounded-xl bg-gradient-to-br ${job.color} flex items-center justify-center flex-shrink-0`}>
                        <Briefcase className="w-6 h-6 text-white" />
                      </div>
                      <div>
                        <h3 className="text-xl font-bold text-white mb-2 group-hover:text-cyan-400 transition-colors">
                          {job.title}
                        </h3>
                        <p className="text-gray-400 mb-4">{job.description}</p>
                        <div className="flex flex-wrap gap-4 text-sm text-gray-500">
                          <span className="flex items-center gap-1">
                            <Briefcase className="w-4 h-4" />
                            {job.department}
                          </span>
                          <span className="flex items-center gap-1">
                            <MapPin className="w-4 h-4" />
                            {job.location}
                          </span>
                          <span className="flex items-center gap-1">
                            <Clock className="w-4 h-4" />
                            {job.type}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                  <button className="inline-flex items-center justify-center px-6 py-3 bg-gradient-to-r from-cyan-500 to-blue-600 text-white font-semibold rounded-xl hover:shadow-lg hover:shadow-cyan-500/25 transition-all whitespace-nowrap">
                    Apply Now
                    <ArrowRight className="ml-2 w-5 h-5" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="relative z-10 py-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto text-center">
          <div className="bg-gradient-to-br from-white/[0.07] to-white/[0.02] border border-white/10 rounded-3xl p-12">
            <h2 className="text-3xl font-bold text-white mb-4">Don't See the Right Role?</h2>
            <p className="text-gray-400 text-lg mb-8 max-w-xl mx-auto">
              We're always looking for talented individuals. Send us your resume and let's talk
              about how you can contribute to our mission.
            </p>
            <Link
              to="/contact"
              className="inline-flex items-center justify-center px-8 py-4 bg-gradient-to-r from-cyan-500 to-blue-600 text-white font-semibold rounded-xl hover:shadow-lg hover:shadow-cyan-500/25 transition-all hover:-translate-y-1"
            >
              Get in Touch
              <ArrowRight className="ml-2 w-5 h-5" />
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
